import { Form, FormProps } from 'react-final-form'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { ReactNode } from 'react'

import { Button } from '../Button'
import { Flex } from '../Flex'
import { Icon } from '../Icons'
import { themeSpace } from '../theme'
import { DialogActions } from '../../redux/dialog'
import { DialogType } from '../../redux/dialog/types'
import { useViewportSize } from '../../hooks'

import { ColumnVisibilitySelector, ColumnVisibilitySelectorProps } from './actions/ColumnVisibilitySelector'
import { QuickFilter } from './QuickFilter'

const StyledTableHeader = styled(Flex)`
  gap: ${themeSpace(3)} 20px;
`

export type TableHeaderProps<T> = {
  onSearchSubmit: FormProps['onSubmit']
  searchLabel: string
  searchPlaceholder: string
  searchName: string
  searchNote?: string
  advancedFilterDialog: DialogType
  isAdvancedFilterActive: boolean
  columnSelectorProps: ColumnVisibilitySelectorProps<T>
  additionalAction?: ReactNode
  validateMin3?: boolean
}

export const TableHeader = <T extends object>({
  onSearchSubmit,
  searchLabel,
  searchPlaceholder,
  searchName,
  searchNote,
  advancedFilterDialog,
  isAdvancedFilterActive,
  columnSelectorProps,
  additionalAction,
  validateMin3 = true
}: TableHeaderProps<T>) => {
  const { t } = useTranslation(['core'])
  const dispatch = useDispatch()
  const viewPortSize = useViewportSize()
  const isMobile = viewPortSize?.isMobile ?? false
  return (
    <StyledTableHeader alignItems="flex-end" flexWrap={isMobile ? 'wrap' : 'nowrap'}>
      <Form
        onSubmit={onSearchSubmit}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit} style={{ flex: '1 1 auto' }}>
            <QuickFilter
              searchLabel={searchLabel}
              searchPlaceholder={searchPlaceholder}
              searchName={searchName}
              searchNote={searchNote}
              width={[1, 8 / 12, 10 / 12]}
              additionalAction={additionalAction}
              validateMin3={validateMin3}
            />
          </form>
        )}
      />

      <Flex
        justifyContent={['center', 'center', 'initial']}
        width={[1, 1, 'auto']}
        alignItems="center"
        minWidth="initial"
      >
        <Button
          icon={<Icon type={isAdvancedFilterActive ? 'FilledFilter' : 'Filter'} />}
          iconPlacement="left"
          onClick={() => dispatch(DialogActions.showDialog({ dialog: advancedFilterDialog }))}
          padding="3px 20px 3px 0"
        >
          {t('core:AdvancedFilter')}
        </Button>
        <ColumnVisibilitySelector {...columnSelectorProps} />
      </Flex>
    </StyledTableHeader>
  )
}
